import { useMemo } from "react";
import { useNvpStatus } from "@/api/hooks";
import { useTeamSyncStatuses, compareGolden, restoreGolden, acceptLiveAsGolden } from "@/api/hooks/use-admin";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { BoatSyncCard } from "@/components/boat-sync-card";
import { asBoatCode } from "@/types/identifiers";
import { timeAgo } from "@/lib/time";
import { useQueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "sailgp:lastEventId";

export function CalibrationSyncPage() {
  const qc = useQueryClient();
  const { data: teams, isLoading } = useTeamSyncStatuses();
  const { getNvpStatus } = useNvpStatus();

  const selectedEventId = localStorage.getItem(STORAGE_KEY);

  const boundAcceptFn = useMemo(() => {
    if (!selectedEventId) return undefined;
    return (boatCode: Parameters<typeof acceptLiveAsGolden>[0]) =>
      acceptLiveAsGolden(boatCode, selectedEventId);
  }, [selectedEventId]);

  const invalidateTeamStatuses = () =>
    qc.invalidateQueries({ queryKey: ["admin", "calibration-sync", "teams"] });

  return (
    <div>
      <PageHeader
        title="Calibration Sync"
        subtitle="Compare golden calibration values against live NVP data per boat."
      />

      {isLoading ? (
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : !teams || teams.length === 0 ? (
        <EmptyState message="No teams found." className="mt-6" />
      ) : (
        <div className="mt-6 flex flex-col gap-2">
          {[...teams]
            .sort((a, b) =>
              (a.boat_code ?? "").localeCompare(b.boat_code ?? ""),
            )
            .map((team) => {
              if (!team.boat_code) return null;
              const boatCode = asBoatCode(team.boat_code);

              return (
                <BoatSyncCard
                  key={boatCode}
                  boatCode={boatCode}
                  teamName={team.team_name}
                  isoAlpha2={team.iso_alpha2}
                  nvpStatus={getNvpStatus(boatCode)}
                  syncType="board-cals"
                  sourceAvailable={team.has_golden}
                  unavailableReason="No golden calibration"
                  sourceLabel={
                    team.has_golden && team.golden_event_name ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400">
                          Golden
                        </span>
                        <span className="text-muted-foreground">
                          from {team.golden_event_name}
                          {team.golden_fetched_at &&
                            ` · ${timeAgo(team.golden_fetched_at)}`}
                        </span>
                      </span>
                    ) : undefined
                  }
                  compareFn={compareGolden}
                  writeBackFn={restoreGolden}
                  writeBackLabel="Restore"
                  expectedHeader={team.golden_event_name ? `Golden from ${team.golden_event_name}` : "Golden"}
                  acceptFn={boundAcceptFn}
                  acceptLabel="Golden?"
                  onAcceptSuccess={invalidateTeamStatuses}
                />
              );
            })}
        </div>
      )}
    </div>
  );
}
